import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AUTH_SIGNED_OUT } from "../api/client";
import type { AcceptInviteOutcome, AcceptInviteRequest, AuthSession, LoginOutcome, MfaRequest } from "../api/auth";
import type { Me } from "../api/types";
import { acceptInvite, completeChallenge, completeOidc, login, logout, me as fetchMe, toMe } from "./session";
import { clearToken } from "./tokenStore";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

export type AuthValue = {
  me: Me | null;
  status: AuthStatus;
  signIn: (email: string, password: string) => Promise<LoginOutcome>;
  signInWithHandoff: (handoff: string) => Promise<LoginOutcome>;
  completeChallenge: (input: MfaRequest) => Promise<Me>;
  acceptInvite: (input: AcceptInviteRequest) => Promise<AcceptInviteOutcome>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth needs an AuthProvider");
  return value;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [me, setMe] = useState<Me | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  const forget = useCallback(() => {
    clearToken();
    queryClient.clear();
    setMe(null);
    setStatus("anonymous");
  }, [queryClient]);

  // A new session must not read another principal's cached rows.
  const adopt = useCallback(
    (session: AuthSession): Me => {
      const next = toMe(session);
      queryClient.clear();
      setMe(next);
      setStatus("authenticated");
      return next;
    },
    [queryClient],
  );

  useEffect(() => {
    let live = true;
    fetchMe().then(
      (session) => {
        if (!live) return;
        setMe(session);
        setStatus("authenticated");
      },
      () => {
        if (live) forget();
      },
    );
    return () => {
      live = false;
    };
  }, [forget]);

  // RequireRole turns "anonymous" into the redirect; this provider sits outside the router.
  useEffect(() => {
    window.addEventListener(AUTH_SIGNED_OUT, forget);
    return () => window.removeEventListener(AUTH_SIGNED_OUT, forget);
  }, [forget]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const outcome = await login(email, password);
      if (outcome.status === "ok") adopt(outcome);
      return outcome;
    },
    [adopt],
  );

  const signInWithHandoff = useCallback(
    async (handoff: string) => {
      const outcome = await completeOidc(handoff);
      if (outcome.status === "ok") adopt(outcome);
      return outcome;
    },
    [adopt],
  );

  const finish = useCallback(async (input: MfaRequest) => adopt(await completeChallenge(input)), [adopt]);

  const accept = useCallback(
    async (input: AcceptInviteRequest) => {
      const outcome = await acceptInvite(input);
      if (outcome.status === "ok") adopt(outcome);
      return outcome;
    },
    [adopt],
  );

  const signOut = useCallback(async () => {
    try {
      await logout();
    } finally {
      forget();
    }
  }, [forget]);

  const value = useMemo<AuthValue>(
    () => ({ me, status, signIn, signInWithHandoff, completeChallenge: finish, acceptInvite: accept, signOut }),
    [me, status, signIn, signInWithHandoff, finish, accept, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
